import React, { useCallback, useEffect, useRef, useState } from "react";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArticleIcon from "@mui/icons-material/Article";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DnsIcon from "@mui/icons-material/Dns";
import KeyIcon from "@mui/icons-material/Key";
import PaidIcon from "@mui/icons-material/Paid";
import TimelineIcon from "@mui/icons-material/Timeline";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Container,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router";

import { ApplicationEndpoints } from "@generated/application";
import { ServicesTab } from "src/domains/application/components/ServicesTab";
import {
  useStackBilling,
  useOrgBilling,
} from "src/domains/application/hooks/useBilling";
import { usePipelineStatus } from "src/domains/application/hooks/useDeploymentEvents";
import { StackRealtimeProvider } from "src/domains/application/hooks/useStackRealtime";
import { useStackStatus } from "src/domains/application/hooks/useStackStatus";
import { Application, StackStatus } from "src/domains/application/types";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { ORGANIZATIONS, PROJECTS } from "src/platform/constants/routes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { usePollingApi } from "src/platform/hooks/usePollingApi";
import { useTabParam } from "src/platform/hooks/useTabParam";
import { ErrorMessage } from "src/shell/components/ErrorMessage";
import { getContainerStyles } from "src/shell/styles/themeUtils";

import {
  ConfigurationTab,
  DeleteDialog,
  EventsTab,
  ManifestPreviewTab,
  OverviewTab,
  PipelineTab,
  SecretsTab,
} from "./components";
import { useEnvironments } from "./components/pipelineDataHooks";
import { StackHeader } from "./components/StackHeader";
import { StackStatusBar } from "./components/StackHeader/StackStatusBar";
import { CostsTab } from "./CostsTab";
import { useStackActions } from "./hooks/useStackActions";
import { useStackValidation } from "./hooks/useStackValidation";
import { StackMetrics, StackTab } from "./types";

// ── Tab icons ──────────────────────────────────────────────────────────────────

const TAB_ICONS: Record<StackTab, React.ReactElement> = {
  overview: <DashboardIcon sx={{ fontSize: 18 }} />,
  pipeline: <AccountTreeIcon sx={{ fontSize: 18 }} />,
  services: <DnsIcon sx={{ fontSize: 18 }} />,
  events: <TimelineIcon sx={{ fontSize: 18 }} />,
  configuration: <TuneIcon sx={{ fontSize: 18 }} />,
  secrets: <KeyIcon sx={{ fontSize: 18 }} />,
  manifest: <ArticleIcon sx={{ fontSize: 18 }} />,
  costs: <PaidIcon sx={{ fontSize: 18 }} />,
};

// ── Tab labels and order ──────────────────────────────────────────────────────

const TAB_LABELS: Record<StackTab, string> = {
  overview: "Summary",
  pipeline: "Pipeline",
  services: "Services",
  events: "Events",
  configuration: "Settings",
  secrets: "Secrets",
  manifest: "Manifest",
  costs: "Costs",
};

const ALL_TABS: StackTab[] = [
  "overview",
  "pipeline",
  "services",
  "events",
  "configuration",
  "secrets",
  "manifest",
  "costs",
];

// Tabs that show a counter badge
const TAB_COUNTERS: Partial<Record<StackTab, "pipeline" | "events">> = {
  pipeline: "pipeline",
  events: "events",
};

function TabCounter({ count }: { count: number }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        bgcolor: "rgba(148,163,184,0.15)",
        fontSize: 11,
        fontWeight: 600,
        color: "text.secondary",
        ml: 0.5,
        px: 0.5,
      }}
    >
      {count}
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function StackDetailsPage() {
  const { orgAlias, projectAlias, sysAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    sysAlias: string;
  }>();
  const nav = useAppNavigation();
  const theme = useTheme();
  const { tab, setTab } = useTabParam<StackTab>("overview");

  const [system, setSystem] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data loading
  const load = useCallback(async () => {
    if (!sysAlias) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION.replace(":id", sysAlias)}`,
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
          },
        },
      );
      setSystem(res.data as Application);
    } catch {
      setError("Stack not found.");
    } finally {
      setLoading(false);
    }
  }, [sysAlias]);

  useEffect(() => {
    void load();
  }, [load]);

  // Metrics polled every 30s
  const metricsUrl = sysAlias
    ? `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_METRICS.replace(":id", sysAlias)}`
    : null;
  const { data: metrics, loading: metricsLoading } =
    usePollingApi<StackMetrics>(metricsUrl, 30_000);

  // WebSocket status updates
  const { status: wsStatus, deploymentUrl: wsDeploymentUrl } =
    useStackStatus(sysAlias);
  // Only update on new WS events (wsStatus change), not on local optimistic updates.
  // Including `system` in the dep array would revert optimistic status changes
  // (e.g., deploy() sets "provisioning" but stale wsStatus="running" would revert it).
  useEffect(() => {
    if (wsStatus) {
      setSystem((s) => (s ? { ...s, status: wsStatus as StackStatus } : s));
    }
  }, [wsStatus]);

  // When status transitions to vcs_provisioned, re-fetch the full application
  // to pick up vcsRepoUrl and other fields that the WS event doesn't carry.
  const prevWsStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      wsStatus === StackStatus.VCS_PROVISIONED &&
      prevWsStatusRef.current !== StackStatus.VCS_PROVISIONED
    ) {
      void load();
    }
    prevWsStatusRef.current = wsStatus;
  }, [wsStatus, load]);

  // When status transitions to "running", re-fetch to pick up deployedAt and
  // other fields that the WS event doesn't carry. Only fire on the TRANSITION
  // (not every time wsStatus is RUNNING) to avoid cascading re-fetches when the
  // WS reconnects and re-sends the same status.
  const prevRunningRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      wsStatus === StackStatus.RUNNING &&
      prevRunningRef.current !== StackStatus.RUNNING
    ) {
      void load();
    }
    prevRunningRef.current = wsStatus;
  }, [wsStatus, load]);

  useEffect(() => {
    if (wsDeploymentUrl) {
      setSystem((s) => (s ? { ...s, deploymentUrl: wsDeploymentUrl } : s));
    }
  }, [wsDeploymentUrl]);

  // Billing
  const {
    data: billingData,
    loading: billingLoading,
    error: billingError,
  } = useStackBilling(sysAlias ?? "");
  const { data: orgBillingData } = useOrgBilling(orgAlias ?? "");

  // Pipeline status for failure detection
  const { steps: pipelineSteps } = usePipelineStatus(sysAlias);

  // Hooks
  const actions = useStackActions({
    sysAlias,
    orgAlias,
    projectAlias,
    system,
    setSystem,
  });

  // Environments for promotion target list (excludes the stack's current env).
  const { environments } = useEnvironments(orgAlias ?? "", projectAlias ?? "");
  const promotionTargets = React.useMemo(() => {
    if (!system || !environments.length) return [];
    return environments
      .filter((env) => env.id !== system.environmentId)
      .map((env) => ({ slug: env.slug, name: env.name }));
  }, [environments, system]);

  const validation = useStackValidation(
    system,
    orgBillingData?.quotaExhausted,
    pipelineSteps,
  );

  // ── REQ-06: Auto-navigate to Pipeline on FAILED / PROVISIONING_FAILED ──────
  // Only redirect once on initial load — never override user tab navigation.
  const autoRedirectedRef = useRef(false);
  const handleTabChange = (_: React.SyntheticEvent, v: StackTab) => {
    setTab(v);
  };

  useEffect(() => {
    if (autoRedirectedRef.current) return;
    if (!system) return;
    if (
      system.status === StackStatus.FAILED ||
      system.status === StackStatus.PROVISIONING_FAILED
    ) {
      autoRedirectedRef.current = true;
      setTab("pipeline");
    }
  }, [system?.status, setTab]);

  // ── Loading / error states ──────────────────────────────────────────────────
  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Skeleton
          variant="rectangular"
          height={120}
          sx={{ mb: 3, borderRadius: 1 }}
        />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Skeleton
                variant="rectangular"
                height={80}
                sx={{ borderRadius: 1 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={300} />
      </Container>
    );
  }

  if (error || !system) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <ErrorMessage message={error ?? "Stack not found"} />
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => window.history.back()}
          sx={{ mt: 2 }}
        >
          Go back
        </Button>
      </Container>
    );
  }

  // Pipeline run count for tab counter
  const pipelineRunCount = 0; // TODO: wire to actual pipeline run count

  return (
    <Container sx={getContainerStyles(theme)}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <RouterLink
            to={
              orgAlias
                ? ORGANIZATIONS.DETAILS.getPath(orgAlias)
                : ORGANIZATIONS.ROOT.path
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {orgAlias}
          </RouterLink>
          {" / "}
          <RouterLink
            to={orgAlias ? PROJECTS.LIST.getPath(orgAlias) : "#"}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Projects
          </RouterLink>
          {" / "}
          <RouterLink
            to={
              orgAlias && projectAlias
                ? PROJECTS.DETAILS.getPath(orgAlias, projectAlias)
                : "#"
            }
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {projectAlias}
          </RouterLink>
          {" / "}
          <Typography component="span" variant="body2" color="text.primary">
            {system.name}
          </Typography>
        </Typography>
      </Box>

      {/* Hero header */}
      <StackHeader
        system={system}
        metrics={metrics}
        actionLoading={actions.actionLoading}
        canDeploy={validation.canDeploy}
        canPause={validation.canPause}
        canResume={validation.canResume}
        isCiControlled={validation.isCiControlled}
        isCiManaged={validation.isCiManaged}
        isCiBuildRushaDeploy={validation.isCiBuildRushaDeploy}
        isRushaBuildCiDeploy={validation.isRushaBuildCiDeploy}
        isRushaOwnedBuild={validation.isRushaOwnedBuild}
        needsConfigure={validation.needsConfigure}
        configReady={validation.configReady}
        configValidations={validation.configValidations}
        isProvisioning={validation.isProvisioning}
        isAdopted={Boolean(system.stackConfig?._adopted)}
        onDeploy={() => void actions.deploy()}
        onPause={() => void actions.pause()}
        onResume={() => void actions.resume()}
        onProvisionVcs={() => void actions.provisionVcs()}
        onBuildAndDeploy={() => void actions.buildAndDeploy()}
        onBuildOnly={() => void actions.buildOnly()}
        onEdit={() => {
          if (system.stackConfig?._adopted) {
            nav.goToStackServices(orgAlias!, projectAlias!, sysAlias!);
          } else {
            nav.goToStackEdit(orgAlias!, projectAlias!, sysAlias!);
          }
        }}
        onDelete={() => {
          actions.setDeleteDialogOpen(true);
        }}
        promotionTargets={promotionTargets}
        onPromote={(slug) => void actions.promote(slug)}
        promoteLoading={actions.promoteLoading}
      />

      {/* Consolidated status bar (REQ-03: at most one alert at a time) */}
      <StackStatusBar
        actionError={actions.actionError}
        onDismissError={actions.clearActionError}
        deployMessage={actions.deployMessage}
        onDismissDeployMessage={actions.clearDeployMessage}
        isProvisioningFailed={validation.isProvisioningFailed}
        isFailed={validation.isFailed}
        failedStep={validation.failedStep}
        isProvisioning={validation.isProvisioning}
        isCiControlled={validation.isCiControlled}
        isCiManaged={validation.isCiManaged}
        configReady={validation.configReady}
        orgBillingLoading={billingLoading}
        configValidations={validation.configValidations}
        isAdopted={Boolean(system.stackConfig?._adopted)}
        onRetryProvisioning={() => void actions.retryProvisioning()}
        retryProvisioningLoading={actions.retryProvisioningLoading}
        onEdit={() => {
          if (system.stackConfig?._adopted) {
            nav.goToStackServices(orgAlias!, projectAlias!, sysAlias!);
          } else {
            nav.goToStackEdit(orgAlias!, projectAlias!, sysAlias!);
          }
        }}
      />

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTabs-indicator": {
            backgroundColor: "#f97316",
            height: 2,
          },
          "& .MuiTab-root": {
            minHeight: 44,
            textTransform: "none",
          },
        }}
      >
        {ALL_TABS.map((t) => (
          <Tab
            key={t}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {TAB_LABELS[t]}
                {TAB_COUNTERS[t] === "pipeline" && (
                  <TabCounter count={pipelineRunCount} />
                )}
                {TAB_COUNTERS[t] === "events" && <TabCounter count={0} />}
              </Box>
            }
            value={t}
            icon={TAB_ICONS[t]}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {/* Tab content */}
      <StackRealtimeProvider
        alias={sysAlias!}
        orgAlias={orgAlias}
        vcsRepoUrl={system.vcsRepoUrl}
      >
        {tab === "overview" && (
          <OverviewTab
            system={system}
            metrics={metrics}
            metricsLoading={metricsLoading}
            onBuildAndDeploy={() => void actions.buildAndDeploy()}
            onTabChange={(t) => setTab(t as StackTab)}
            environments={environments.map((e) => ({
              slug: e.slug,
              name: e.name,
            }))}
            onPromote={(slug) => void actions.promote(slug)}
            billingData={billingData}
            orgAlias={orgAlias}
          />
        )}
        {tab === "pipeline" && (
          <PipelineTab
            alias={sysAlias!}
            templateSlug={system.templateSlug ?? undefined}
            deployMode={system.deployMode}
            orgAlias={orgAlias}
            vcsRepoUrl={system.vcsRepoUrl}
            status={system.status}
            projectAlias={projectAlias}
            onProvisionVcs={() => void actions.provisionVcs()}
            onPromote={(slug) => void actions.promote(slug)}
          />
        )}
        {tab === "services" && (
          <ServicesTab
            alias={sysAlias!}
            onRunPipeline={() => void actions.buildAndDeploy()}
          />
        )}
        {tab === "events" && <EventsTab alias={sysAlias!} />}
        {tab === "configuration" && (
          <ConfigurationTab
            system={system}
            onDeleteProtectionChange={actions.deleteProtectionChange}
            deleteProtectionLoading={actions.deleteProtectionLoading}
            onAutoDeployChange={actions.autoDeployChange}
            autoDeployLoading={actions.autoDeployLoading}
            onRetryProvisioning={actions.retryProvisioning}
            retryProvisioningLoading={actions.retryProvisioningLoading}
          />
        )}
        {tab === "secrets" && <SecretsTab system={system} />}
        {tab === "manifest" && <ManifestPreviewTab system={system} />}
        {tab === "costs" && (
          <CostsTab
            alias={sysAlias!}
            data={billingData}
            loading={billingLoading}
            error={billingError}
          />
        )}
      </StackRealtimeProvider>

      <Box sx={{ mt: 4 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={
            orgAlias && projectAlias
              ? PROJECTS.DETAILS.getPath(orgAlias, projectAlias)
              : "#"
          }
        >
          Back to project
        </Button>
      </Box>

      <DeleteDialog
        open={actions.deleteDialogOpen}
        systemName={system.name}
        deleteProtection={system.deleteProtection ?? false}
        onClose={() => actions.setDeleteDialogOpen(false)}
        onSoftDelete={actions.softDelete}
        onHardDelete={actions.hardDelete}
        loading={actions.actionLoading}
      />
    </Container>
  );
}
